import React, { useState } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const NotificationPreferences = ({ initialPreferences, onSave, className = '' }) => {
  const [preferences, setPreferences] = useState({
    emailNotifications: {
      appointments: initialPreferences?.emailNotifications?.appointments ?? true,
      reminders: initialPreferences?.emailNotifications?.reminders ?? true,
      marketing: initialPreferences?.emailNotifications?.marketing ?? false,
      systemUpdates: initialPreferences?.emailNotifications?.systemUpdates ?? true,
      reports: initialPreferences?.emailNotifications?.reports ?? true
    },
    smsNotifications: {
      appointments: initialPreferences?.smsNotifications?.appointments ?? true,
      reminders: initialPreferences?.smsNotifications?.reminders ?? false,
      marketing: initialPreferences?.smsNotifications?.marketing ?? false,
      emergencies: initialPreferences?.smsNotifications?.emergencies ?? true
    },
    pushNotifications: {
      realTime: initialPreferences?.pushNotifications?.realTime ?? true,
      daily: initialPreferences?.pushNotifications?.daily ?? false,
      weekly: initialPreferences?.pushNotifications?.weekly ?? true
    }
  });

  const [isLoading, setIsLoading] = useState(false);

  const handlePreferenceChange = (category, key, value) => {
    setPreferences(prev => ({
      ...prev,
      [category]: {
        ...prev?.[category],
        [key]: value
      }
    }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      onSave?.(preferences);
    } catch (error) {
      console.error('Error saving preferences:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const notificationSections = [
    {
      title: 'اعلان‌های ایمیل',
      icon: 'Mail',
      category: 'emailNotifications',
      items: [
        { key: 'appointments', label: 'نوبت‌های جدید', description: 'اطلاع از رزرو نوبت‌های جدید' },
        { key: 'reminders', label: 'یادآوری‌ها', description: 'یادآوری نوبت‌های آینده' },
        { key: 'reports', label: 'گزارش‌های روزانه', description: 'خلاصه فعالیت‌های روزانه' },
        { key: 'systemUpdates', label: 'به‌روزرسانی‌های سیستم', description: 'اطلاع از تغییرات و بهبودها' },
        { key: 'marketing', label: 'پیام‌های تبلیغاتی', description: 'اطلاع از تخفیف‌ها و پیشنهادات' }
      ]
    },
    {
      title: 'اعلان‌های پیامکی',
      icon: 'MessageSquare',
      category: 'smsNotifications',
      items: [
        { key: 'appointments', label: 'نوبت‌های جدید', description: 'پیامک برای رزرو نوبت‌های جدید' },
        { key: 'reminders', label: 'یادآوری‌ها', description: 'یادآوری نوبت‌های آینده' },
        { key: 'emergencies', label: 'موارد اضطراری', description: 'اطلاع‌رسانی فوری' },
        { key: 'marketing', label: 'پیام‌های تبلیغاتی', description: 'پیامک تخفیف‌ها و پیشنهادات' }
      ]
    },
    {
      title: 'اعلان‌های فوری',
      icon: 'Bell',
      category: 'pushNotifications',
      items: [
        { key: 'realTime', label: 'اعلان‌های لحظه‌ای', description: 'دریافت اعلان‌های فوری' },
        { key: 'daily', label: 'خلاصه روزانه', description: 'خلاصه فعالیت‌های روز' },
        { key: 'weekly', label: 'گزارش هفتگی', description: 'خلاصه فعالیت‌های هفته' }
      ]
    }
  ];

  return (
    <div className={`space-y-8 ${className}`}>
      <div className="bg-muted/50 p-4 rounded-lg">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-foreground mb-1">
              درباره اعلان‌ها
            </h4>
            <p className="text-xs text-muted-foreground">
              می‌توانید نوع و زمان دریافت اعلان‌ها را بر اساس نیاز خود تنظیم کنید. این تنظیمات در هر زمان قابل تغییر است.
            </p>
          </div>
        </div>
      </div>
      {notificationSections?.map((section) => (
        <div key={section?.category} className="space-y-4">
          <div className="flex items-center gap-3 pb-2 border-b border-border">
            <Icon name={section?.icon} size={20} className="text-primary" />
            <h3 className="text-lg font-medium text-foreground">
              {section?.title}
            </h3>
          </div>

          <div className="space-y-4">
            {section?.items?.map((item) => (
              <div key={item?.key} className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/30 transition-colors duration-150">
                <Checkbox
                  checked={preferences?.[section?.category]?.[item?.key]}
                  onChange={(e) => handlePreferenceChange(section?.category, item?.key, e?.target?.checked)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <label className="text-sm font-medium text-foreground cursor-pointer">
                    {item?.label}
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    {item?.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="flex justify-end gap-4 pt-6 border-t border-border">
        <Button
          variant="outline"
          disabled={isLoading}
        >
          بازگردانی پیش‌فرض
        </Button>
        
        <Button
          onClick={handleSave}
          loading={isLoading}
          iconName="Save"
          iconPosition="right"
        >
          ذخیره تنظیمات
        </Button>
      </div>
    </div>
  );
};

export default NotificationPreferences;